import { CheckCircle2, Lock, Save, TriangleAlert } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { authFetch } from '../auth'
import { usePlatformRegistry, useStoreCostProfiles } from '../platform'
import type { StoreCostVersion } from '../types'

const FIELD_LABELS: Record<string, string> = {
  packagingCost: '包装成本',
  giftCost: '赠品成本',
  shippingInsuranceFee: '运费险',
  shippingProviderName: '快递/履约方式',
  shippingFee: '运费',
  platformFeeRate: '平台扣点',
  taxRate: '税点',
  talentCommissionRate: '达人直播佣金',
}

type Draft = Record<
  'effectiveDate' | 'packagingCost' | 'giftCost' | 'shippingInsuranceFee' | 'shippingProviderCode'
  | 'shippingProviderName' | 'shippingFee' | 'platformFeeRate' | 'taxRate' | 'talentCommissionRate',
  string
>

const today = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date())

const draftFrom = (version: StoreCostVersion | undefined): Draft => ({
  effectiveDate: version?.effectiveDate || today(),
  packagingCost: version?.packagingCost?.toString() || '',
  giftCost: version?.giftCost?.toString() || '',
  shippingInsuranceFee: version?.shippingInsuranceFee?.toString() || '',
  shippingProviderCode: version?.shippingProviderCode || '',
  shippingProviderName: version?.shippingProviderName || '',
  shippingFee: version?.shippingFee?.toString() || '',
  platformFeeRate: version?.platformFeeRate?.toString() || '',
  taxRate: version?.taxRate?.toString() || '',
  talentCommissionRate: version?.talentCommissionRate?.toString() || '',
})

const display = (value: number | null, suffix = '') => value === null ? '—' : `${value.toFixed(2)}${suffix}`

export function StoreCostProfilePage({ storeId }: { storeId: string }) {
  const registry = usePlatformRegistry()
  const profiles = useStoreCostProfiles()
  const store = registry.data?.stores?.find((item) => (item.storeId || item.id) === storeId)
  const profile = profiles.data?.stores.find((item) => item.storeId === storeId)
  const businessDate = today()
  const latest = useMemo(() => [...(profile?.defaultVersions || [])]
    .filter((version) => version.effectiveDate <= businessDate)
    .sort((left, right) => right.effectiveDate.localeCompare(left.effectiveDate))[0], [businessDate, profile])
  const upcoming = useMemo(() => [...(profile?.defaultVersions || [])]
    .filter((version) => version.effectiveDate > businessDate)
    .sort((left, right) => left.effectiveDate.localeCompare(right.effectiveDate)), [businessDate, profile])
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Draft>(() => draftFrom(latest))
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => { if (!editing) setDraft(draftFrom(latest)) }, [editing, latest])

  if (registry.loading || profiles.loading) return <div className="loading-state">正在加载店铺成本档案…</div>
  if (registry.error || profiles.error || !store || !profile) {
    return <div className="error-state"><h1>成本档案不可用</h1><p>{registry.error || profiles.error || '店铺成本档案不存在'}</p></div>
  }

  const storeName = store.displayName || store.name || storeId
  const isPdd = profile.platformId === 'pinduoduo'
  const isDouyin = profile.platformId === 'douyin'
  const set = (field: keyof Draft, value: string) => setDraft((current) => ({ ...current, [field]: value }))
  const save = async () => {
    setSaving(true); setError(''); setMessage('')
    try {
      const response = await authFetch('/api/store-cost-profiles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId,
          ...draft,
          ...Object.fromEntries([
            'packagingCost', 'giftCost', 'shippingInsuranceFee', 'shippingFee', 'platformFeeRate', 'taxRate', 'talentCommissionRate',
          ].map((field) => [field, draft[field as keyof Draft].trim() === '' ? null : Number(draft[field as keyof Draft])])),
        }),
      })
      const payload = await response.json() as { error?: string }
      if (!response.ok) throw new Error(payload.error || `保存失败：${response.status}`)
      await profiles.reload()
      setEditing(false)
      setMessage('店铺成本版本已保存。')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page-stack store-cost-page">
      <div className="page-title-row">
        <div><h1>{storeName} · 店铺成本</h1><p>公司采购成本共用；这里只维护该店铺自己的履约、扣点和税费。</p></div>
        <button className="primary-button" type="button" onClick={() => editing ? save() : setEditing(true)} disabled={saving}>
          {editing ? <Save aria-hidden="true" /> : <Lock aria-hidden="true" />}{saving ? '保存中…' : editing ? '保存成本版本' : '解锁编辑'}
        </button>
      </div>

      <section className={`cost-readiness-card ${latest?.calculationReady ? 'is-ready' : 'is-missing'}`}>
        {latest?.calculationReady ? <CheckCircle2 aria-hidden="true" /> : <TriangleAlert aria-hidden="true" />}
        <div>
          <strong>{latest?.calculationReady ? '店铺默认成本已配置' : '店铺成本尚未完整配置'}</strong>
          <p>{latest?.calculationReady
            ? `当前版本自 ${latest.effectiveDate} 生效；商品覆盖可在店铺商品页继续细化。`
            : '缺失项不会按0处理，利润功能保持关闭，避免生成虚假利润。'}</p>
        </div>
      </section>
      {upcoming.length > 0 && <p className="future-cost-note">已保存 {upcoming.length} 个未来成本版本，最近一版自 {upcoming[0].effectiveDate} 生效；生效前不影响当前或历史利润。</p>}

      {!editing && latest ? (
        <section className="store-cost-summary-grid">
          <div><span>包装</span><strong>{display(latest.packagingCost)}</strong></div>
          <div><span>赠品</span><strong>{display(latest.giftCost)}</strong></div>
          <div><span>运费险</span><strong>{display(latest.shippingInsuranceFee)}</strong></div>
          <div><span>{latest.shippingProviderName || '履约方式'}</span><strong>{display(latest.shippingFee)}</strong></div>
          <div><span>平台扣点</span><strong>{display(latest.platformFeeRate, '%')}</strong></div>
          <div><span>税点</span><strong>{display(latest.taxRate, '%')}</strong></div>
          {isDouyin && <div><span>达人直播佣金</span><strong>{display(latest.talentCommissionRate ?? null, '%')}</strong></div>}
        </section>
      ) : editing ? (
        <section className="store-cost-form">
          <label><span>生效日期</span><input type="date" value={draft.effectiveDate} onChange={(event) => set('effectiveDate', event.target.value)} /></label>
          <label><span>包装成本/件</span><input type="number" min="0" step="0.01" value={draft.packagingCost} onChange={(event) => set('packagingCost', event.target.value)} /></label>
          <label><span>赠品成本/件</span><input type="number" min="0" step="0.01" value={draft.giftCost} onChange={(event) => set('giftCost', event.target.value)} /></label>
          <label><span>运费险/件</span><input type="number" min="0" step="0.01" value={draft.shippingInsuranceFee} onChange={(event) => set('shippingInsuranceFee', event.target.value)} /></label>
          <label><span>快递/履约名称</span><input value={draft.shippingProviderName} placeholder={isPdd ? '例如：商家快递' : isDouyin ? '未确认' : '京东快递'} onChange={(event) => { set('shippingProviderName', event.target.value); set('shippingProviderCode', event.target.value.trim() ? (isPdd ? 'merchant-express' : draft.shippingProviderCode) : '') }} /></label>
          <label><span>运费/件</span><input type="number" min="0" step="0.01" value={draft.shippingFee} onChange={(event) => set('shippingFee', event.target.value)} /></label>
          <label><span>平台扣点%</span><input type="number" min="0" max="100" step="0.01" value={draft.platformFeeRate} onChange={(event) => set('platformFeeRate', event.target.value)} /></label>
          <label><span>税点%</span><input type="number" min="0" max="100" step="0.01" value={draft.taxRate} onChange={(event) => set('taxRate', event.target.value)} /></label>
          {isDouyin && <label><span>达人直播佣金(%)</span><input type="number" min="0" max="100" step="0.01" value={draft.talentCommissionRate} onChange={(event) => set('talentCommissionRate', event.target.value)} /></label>}
        </section>
      ) : (
        <div className="empty-state store-cost-empty">
          <strong>待业务确认后录入</strong>
          <p>需要确认：{(profile.missingFields || []).map((field) => FIELD_LABELS[field] || field).join('、')}。</p>
        </div>
      )}

      {editing && <div className="form-actions"><button type="button" onClick={() => setEditing(false)}>取消</button><button className="primary-button" type="button" onClick={save} disabled={saving}><Save aria-hidden="true" />保存成本版本</button></div>}
      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{error}</p>}
      <section className="cost-layer-note">
        <strong>成本取值顺序</strong><p>实际结算 ＞ 店铺商品覆盖 ＞ 店铺默认成本 ＞ 已确认的平台模板。任何一层缺失都保持缺失，不补0。</p>
        {isPdd && <p>拼多多商品成本按已确认SKU编码关联公司产品；未关联或缺产品成本的商品继续保持未覆盖。</p>}
        {isDouyin && <p>抖音商品成本按已接编码SKU关联公司产品；未接编码的金额不摊成本，单列「缺成本」。达人合作场佣金优先取罗盘预估佣金真实值，缺失时按此处达人直播佣金比例兜底。</p>}
        {!isPdd && !isDouyin && <p>京东SKU级快递和费用覆盖继续在 <Link to="/product-master">京东SKU资料</Link> 中维护。</p>}
      </section>
      {isPdd && profile.afterSalesCostPolicy && <section className="cost-layer-note">
        <strong>二退成本规则</strong>
        <p>二退订单按订单去重；往返运费＝当前生效运费 × {profile.afterSalesCostPolicy.secondReturnFreightMultiplier.toFixed(0)}，包装盒按当前包装成本全额损耗，预计产品货损＝产品成本 × {profile.afterSalesCostPolicy.secondReturnDamageRate.toFixed(0)}%。退回商品默认重新入库再售，不扣整件产品成本，只估部分折损；一退不计损失。</p>
        <p>该规则自 {profile.afterSalesCostPolicy.effectiveDate} 起用于售后申请日期口径；订单编号仅在导入时去重，不保存。</p>
      </section>}
    </div>
  )
}
